import { styled } from "goober";
import { type ReactNode, useState } from "react";
import { HelpTooltip } from "../HelpTooltip";
import { metaJoin } from "../MetaSeparator";
import { CopyableValue } from "../Settings/CopyableValue";
import {
  DetailLabel,
  DetailRow,
  DetailRowRight,
  RowNameLine,
} from "../Settings/shared";
import { StatusBadge } from "../StatusBadge";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { type CertificateDescription, coversHost } from "./describeCertificate";
import { DisclosureToggle } from "./DisclosureToggle";

const EXPIRY_WARNING_MS = 30 * 24 * 60 * 60 * 1000;

const SOURCE_LABELS = {
  presented: "Presented by the server",
  aia: "Fetched from the issuer’s AIA URL",
};

const CertificateGroup = styled("div")`
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  & > div:not(:last-child) {
    border-bottom: none;
  }
  &[data-expanded] {
    padding-bottom: 4px;
  }
`;

const formatDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString("en-US", {
    dateStyle: "medium",
    timeZone: "UTC",
  });

function expiryWarning(
  description: CertificateDescription,
  now: number,
  trusted: boolean
): { color: "orange" | "red"; label: string; detail: string } | null {
  if (now < description.notBefore) {
    return {
      color: "orange",
      label: "Not yet valid",
      detail: `Not valid until ${formatDate(description.notBefore)}.`,
    };
  }
  if (now > description.notAfter) {
    return {
      color: "red",
      label: "Expired",
      detail: `Expired on ${formatDate(description.notAfter)}.${trusted ? " 3B still trusts this certificate." : ""}`,
    };
  }
  if (description.notAfter - now <= EXPIRY_WARNING_MS) {
    return {
      color: "orange",
      label: "Expires soon",
      detail: `Expires on ${formatDate(description.notAfter)}.`,
    };
  }
  return null;
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DetailLabel>
      <Text variant="text-small-md">{label}</Text>
      <Text variant="text-small" color="var(--text-secondary)">
        {children}
      </Text>
    </DetailLabel>
  );
}

export function CertificateDetails({
  description,
  now,
  tone,
  host,
  source,
  issuer,
  actions,
  detailActions,
  guidance,
}: {
  description: CertificateDescription;
  now: number;
  tone: "trusted" | "attention" | "neutral";
  host?: string;
  source?: "presented" | "aia";
  issuer?: boolean;
  actions?: ReactNode;
  detailActions?: ReactNode;
  guidance?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const trusted = tone === "trusted";
  const expiry = expiryWarning(description, now, trusted);
  const nameMismatch =
    host !== undefined &&
    !coversHost(description.subjectAlternativeNames, host);
  return (
    <CertificateGroup
      role="group"
      aria-label={description.commonName}
      data-expanded={expanded || undefined}
    >
      <DetailRow>
        <DetailLabel style={{ flex: 1 }}>
          <RowNameLine>
            <Text variant="text-regular-md" ellipsis>
              {description.commonName}
            </Text>
            {trusted && <StatusBadge color="green">Trusted</StatusBadge>}
            {tone === "attention" && (
              <StatusBadge color="orange">Not trusted</StatusBadge>
            )}
            {description.selfSigned && (
              <StatusBadge color="yellow">Self-signed</StatusBadge>
            )}
            {expiry && (
              <Tooltip content={expiry.detail}>
                <StatusBadge color={expiry.color}>{expiry.label}</StatusBadge>
              </Tooltip>
            )}
            {nameMismatch && (
              <Tooltip
                content={`The subject alternative names don’t cover ${host}.`}
              >
                <StatusBadge color="orange">Name mismatch</StatusBadge>
              </Tooltip>
            )}
            {description.isCa && <StatusBadge color="gray">CA</StatusBadge>}
            {description.isCa && !issuer && (
              <HelpTooltip content="This certificate is marked as a certificate authority (CA:TRUE). Trusting it pins this exact certificate only." />
            )}
          </RowNameLine>
          <Text variant="text-small" color="var(--text-secondary)">
            {metaJoin([
              `Issued by ${description.issuerCommonName}`,
              source && SOURCE_LABELS[source],
              `${now > description.notAfter ? "Expired" : "Expires"} ${formatDate(description.notAfter)}`,
            ])}
          </Text>
          {guidance && (
            <Text variant="text-small" color="var(--text-secondary)">
              {guidance}
            </Text>
          )}
        </DetailLabel>
        <DetailRowRight>
          {actions}
          <DisclosureToggle
            variant="secondary"
            icon="chevron-down"
            aria-label={
              expanded ? "Hide certificate details" : "Show certificate details"
            }
            aria-expanded={expanded}
            onPress={() => setExpanded((was) => !was)}
          />
        </DetailRowRight>
      </DetailRow>
      {expanded && (
        <DetailRow style={{ paddingTop: 0 }}>
          <DetailLabel style={{ flex: 1, gap: 12 }}>
            <Detail label="Subject">{description.subject}</Detail>
            <Detail label="Issuer">{description.issuer}</Detail>
            <Detail label="Valid">
              {formatDate(description.notBefore)} to{" "}
              {formatDate(description.notAfter)}
            </Detail>
            {description.subjectAlternativeNames.length > 0 && (
              <Detail label="Subject alternative names">
                {description.subjectAlternativeNames.join(", ")}
              </Detail>
            )}
            <DetailLabel style={{ gap: 8 }}>
              <Text variant="text-small-md">SHA-256 fingerprint</Text>
              <CopyableValue value={description.sha256}>
                <Text variant="code-small" ellipsis>
                  {description.sha256}
                </Text>
              </CopyableValue>
            </DetailLabel>
            {detailActions}
          </DetailLabel>
        </DetailRow>
      )}
    </CertificateGroup>
  );
}
